/**
 * Pré-carregamento seletivo de áreas: baixa o código de uma área essencial em
 * segundo plano para que ela continue abrindo se a conexão cair depois.
 *
 * Só roda com conexão, fora do modo de economia de dados e em redes que não
 * sejam 2G. Assim o carregamento inicial continua enxuto e conexões limitadas
 * não pagam o download antecipado.
 */

interface NetworkInformationLike {
  saveData?: boolean;
  effectiveType?: string;
}

const CONSTRAINED_EFFECTIVE_TYPES = new Set(["slow-2g", "2g"]);
const IDLE_FALLBACK_DELAY_MS = 2000;

export function canPreloadOnCurrentConnection(): boolean {
  if (typeof navigator === "undefined") return false;
  if (navigator.onLine === false) return false;
  const connection = (navigator as Navigator & {
    connection?: NetworkInformationLike;
  }).connection;
  if (connection?.saveData) return false;
  if (
    connection?.effectiveType &&
    CONSTRAINED_EFFECTIVE_TYPES.has(connection.effectiveType)
  ) {
    return false;
  }
  return true;
}

/**
 * Agenda `preload` para quando o navegador estiver ocioso, depois que a tela
 * atual terminar de carregar. Retorna a função de cancelamento.
 */
export function preloadWhenIdle(preload: () => Promise<unknown>): () => void {
  if (typeof window === "undefined") return () => undefined;
  let cancelled = false;
  const run = () => {
    if (cancelled || !canPreloadOnCurrentConnection()) return;
    void preload();
  };

  const idleWindow = window as Window & {
    requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
    cancelIdleCallback?: (handle: number) => void;
  };
  if (typeof idleWindow.requestIdleCallback === "function") {
    const handle = idleWindow.requestIdleCallback(run, {
      timeout: IDLE_FALLBACK_DELAY_MS * 5,
    });
    return () => {
      cancelled = true;
      idleWindow.cancelIdleCallback?.(handle);
    };
  }
  const timer = window.setTimeout(run, IDLE_FALLBACK_DELAY_MS);
  return () => {
    cancelled = true;
    window.clearTimeout(timer);
  };
}
